import { Preference } from '@app/core/model/user-preferences.interface'
import { LanguageService } from '@app/core/service/language.service'
import { FeeAmountType, FeeItemInstance } from '@app/features/scope-overview/model/fee-item.model'
import { Money } from '@app/features/scope-overview/model/money.model'
import { ScopeSection } from '@app/features/scope-overview/model/scope-section'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { TableColumnKey } from '../../ui-components/scope-ui-table/table-column-key.enum'
import { ScopeVersion } from '@app/core/model/scope-version'
import { toFixedNumber } from '@app/shared/utils/utils'

type FeeParentEntity = ScopeVersion | ScopeSection | Deliverable | ScopeComponent

export const getFeeColumns = (langService: LanguageService) => [
  {
    key: TableColumnKey.FEE_NAME,
    name: `${langService.get('scope|u')} / ${langService.get('deliverable|u')} / ${langService.get('component|u')} FEE`,
    valueIconFunction: () => 'request_quote',
    selected: true,
    isVisibleInList: () => false,
    value: (feeItem: FeeItemInstance) => feeItem.feeItem?.name,
    footerRowValue: (parentEntity: FeeParentEntity) =>
      parentEntity.feeItems?.length ? 'Fee total' : 'No fees defined',
  },
  {
    key: TableColumnKey.AMOUNT,
    name: 'Amount',
    selected: true,
    isVisibleInList: () => false,
    value: (feeItem: FeeItemInstance, parentEntity: FeeParentEntity) =>
      feeItem.feeItem?.amountType === FeeAmountType.PERCENTAGE
        ? feeItem.feeItem?.amount
        : new Money(feeItem.feeItem?.amount, feeItem.currencyUnit || parentEntity?.currencyUnit),
  },
  {
    key: TableColumnKey.PRICE,
    name: 'Price',
    selected: true,
    isVisibleInList: () => false,
    value: (feeItem: FeeItemInstance, parentEntity: FeeParentEntity) => {
      return new Money(getFeePrice(feeItem, parentEntity), feeItem?.currencyUnit)
    },
    footerRowValue: (parentEntity: FeeParentEntity) =>
      new Money(parentEntity.getTotalFees(), parentEntity.currencyUnit),
  },
]

export function getFeeColumnsWithName(columns: Preference[], name: string): Preference[] {
  const feeColumns = columns.map((x) => Object.assign({}, x))
  feeColumns[0].name = name
  return feeColumns
}

export function getTotalFees(parentEntity: FeeParentEntity): number {
  if (!parentEntity?.feeItems?.length) return 0

  const total = parentEntity.feeItems.reduce((sum, feeItem) => sum + getFeePrice(feeItem, parentEntity), 0)

  return toFixedNumber(total)
}

function getFeePrice(feeItem: FeeItemInstance, parentEntity: FeeParentEntity): number {
  if (!feeItem) return 0

  if (parentEntity?.calculatedValues && feeItem.feeItem) {
    const { amount, amountType } = feeItem.feeItem
    const value =
      amountType === FeeAmountType.PERCENTAGE
        ? (parentEntity.calculatedValues?.naturalSellingPrice || 0) * (amount / 100)
        : amount

    return toFixedNumber(value)
  }

  const value = feeItem?.price || 0
  return toFixedNumber(value)
}
